import React, {useState, useEffect, useRef, useContext, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import classNames from 'classnames';
import {Dialog, DialogContent, DialogContentText, DialogActions, Button} from '@material-ui/core';

import Conf from 'utils/Conf';
import Context from 'utils/Context';
import {useViewerTimings, useChat} from 'hooks';
import CartoucheSeance from 'components/CartoucheSeance';
import LanguageSelector from 'components/LanguageSelector';
import Chat from 'components/Chat/Chat';

import './VirtualRoom.scss';
import VirtualRoomScreen from './VirtualRoomScreen';

const TARGET_DIMENSIONS = {
  width: 1920,
  height: 1080,
};

const VirtualRoom = ({seance, obtainAWSAuthCookie}) => {
  const salleRef = useRef(null);
  const {appState} = useContext(Context);
  const {t} = useTranslation();

  const [windowDimensions, setWindowDimensions] = useState({
    innerHeight: window.innerHeight,
    innerWidth: window.innerWidth,
  });

  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const [videoNeedInteraction, setVideoNeedInteraction] = useState(false);

  const {messages, handleMessageSend} = useChat(seance.event_id);

  const viewerTimings = useViewerTimings(seance);
  const {chattingAvailable, lightsOff, screenOn, seanceStatus, currentVideo} = viewerTimings;

  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        innerHeight: window.innerHeight,
        innerWidth: window.innerWidth,
      });
    };

    const handleFullscreenChange = () => {
      if (document.fullscreenElement) {
        setIsFullScreen(true);
        document.body.classList.add('fullscreen');
      } else {
        setIsFullScreen(false);
        document.body.classList.remove('fullscreen');
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // If user has never interracted with the domain, autoplay video won't work.
  // So, we present the user a dialog for him to click to launch the playlist
  const playerRef = useCallback(player => {
    if (!player) return;
    const playPromise = player.play();
    if (playPromise === undefined) {
      setVideoNeedInteraction(true);
      return;
    }
    playPromise
      .then(() => {
        player.pause();
      })
      .catch(() => {
        setVideoNeedInteraction(true);
      });
  }, []);

  const getPositionningValues = () => {
    const {innerWidth, innerHeight} = windowDimensions;

    // If screen is wider than target ratio (16/9)
    if (innerWidth / innerHeight >= TARGET_DIMENSIONS.width / TARGET_DIMENSIONS.height) {
      const magnification = innerHeight / TARGET_DIMENSIONS.height;
      return {
        left: (innerWidth - TARGET_DIMENSIONS.width * magnification) / 2,
        top: 0,
        magnification,
      };
    }

    const magnification = innerWidth / TARGET_DIMENSIONS.width;
    return {
      left: 0,
      top: (innerHeight - TARGET_DIMENSIONS.height * magnification) / 2,
      magnification,
    };
  };

  const toggleFullScreen = e => {
    e.stopPropagation();
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      // document.body.requestFullscreen();
    }
  };

  const {magnification, top, left} = getPositionningValues();

  const filmEnded = seanceStatus === 'ended' || (currentVideo && currentVideo.name === 'live');

  return (
    <div className="seance-wrapper">
      <div
        className={classNames('seance-container', {
          started: screenOn,
          'film-ended': filmEnded,
          'lights-off': lightsOff && !filmEnded,
        })}
        ref={salleRef}
        onDoubleClick={screenOn ? toggleFullScreen : undefined}
      >
        <div
          className={classNames('seance-container-inner', {show: screenOn})}
          style={{
            transform: `scale(${magnification})`,
            top,
            left,
          }}
        >
          {!!seance.event_id && chattingAvailable && (
            <div className="chat-container">
              <Chat
                onSend={handleMessageSend}
                messages={messages}
                notFull={screenOn && (!filmEnded || isFullScreen)}
                visible={!screenOn || (filmEnded && !isFullScreen)}
              />
            </div>
          )}
          {!videoNeedInteraction && (
            <VirtualRoomScreen
              seance={seance}
              onTogglePause={() => setIsPaused(paused => !paused)}
              timings={viewerTimings}
              obtainAWSAuthCookie={obtainAWSAuthCookie}
            />
          )}
          {!screenOn && (
            <div className="seance-waiting-poster">
              <CartoucheSeance hideBtns seance={seance || null} />
            </div>
          )}
          <div
            className={classNames('screen-lang-selector', {
              show: seanceStatus !== 'playing' || (!filmEnded && isPaused) || filmEnded,
            })}
          >
            <LanguageSelector appState={appState} container={salleRef.current} />
          </div>
        </div>
      </div>
      <video className="autoplay-test-video" ref={playerRef} src={`${Conf.assetsRoot}/25eh.mp4`} playsInline />
      <Dialog open={videoNeedInteraction} onClose={() => setVideoNeedInteraction(false)} className="my-dialog">
        <DialogContent>
          <DialogContentText>{t('bienvenue-dans-votre-salle-de-cinema-virtuelle')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => setVideoNeedInteraction(false)}>
            {t('entrer')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default VirtualRoom;
